import type { Any } from '@api/orvalFetcher'
import type { SocketConfigLite } from '@store/modules/sockets/slice'

export type FullSocketConfig = Omit<SocketConfigLite, 'match'> & {
  match: Array<string | RegExp>
  subscription?: string | Record<string, unknown> | ((path: string) => string | Record<string, unknown>)
  onConnect?: (subscription: Any, path: string) => (() => void) | undefined
}

/**
 * Strips query parameters and fragments from a path.
 */
export const cleanPath = (rawPath: string): string => {
  if (!rawPath) return '/'

  return rawPath.split(/[?#]/)[0] || '/'
}

/**
 * Matches a path against a string pattern.
 * Supports exact match, wildcards (*), and dynamic parameters (:id).
 */
export const matchStringPattern = (pattern: string, rawPath: string): boolean => {
  if (!pattern || !rawPath) return false

  const path = rawPath.replace(/^\/+|\/+$/g, '')
  const pat = pattern.replace(/^\/+|\/+$/g, '')

  // 1. Exact match (fast path)
  if (!pat.includes(':') && !pat.endsWith('*')) {
    return path === pat
  }

  // 2. Wildcard pattern
  if (pat.endsWith('*')) {
    const base = pat.slice(0, -1)

    return path === base || path.startsWith(base + '/')
  }

  // 3. Dynamic parameter pattern
  const pathSegments = path.split('/')
  const patSegments = pat.split('/')

  if (pathSegments.length !== patSegments.length) return false

  return patSegments.every((seg, i) => seg.startsWith(':') || seg === pathSegments[i])
}

/**
 * Matches a single matcher (string | RegExp) against a path.
 */
export const matchesOne = (matcher: string | RegExp, path: string): boolean => {
  if (matcher instanceof RegExp) {
    return matcher.test(path)
  }

  if (matcher.startsWith('/') && matcher.endsWith('/') && matcher.length > 2) {
    try {
      return new RegExp(matcher.slice(1, -1)).test(path)
    } catch (e) {
      console.error(`Invalid regex string: ${matcher}`, e)

      return false
    }
  }

  return matchStringPattern(matcher, path)
}

/**
 * Determines if a socket configuration should be active for a given path.
 */
export const shouldSocketBeActive = (config: FullSocketConfig, path: string): boolean => {
  const matchers = Array.isArray(config.match) ? config.match : [config.match]

  return matchers.some((matcher) => matchesOne(matcher, path))
}
